import API from '../config/api';
import React, { useEffect, useState } from 'react';

import axios from 'axios';

import { Container, Row, Col, Card, Badge, Button, Modal } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FaEye } from 'react-icons/fa';

import CustomNavbar from '../components/CustomNavbar';

/* Everything the UI needs to know about each refund status in one place */
const STATUS_META = {
  Pending: { color: '#f59e0b', bg: 'warning', icon: '⏳', step: 1 },
  Approved: { color: '#22c55e', bg: 'success', icon: '✅', step: 2 },
  Refunded: { color: '#3b82f6', bg: 'primary', icon: '💵', step: 3 },
  Rejected: { color: '#ef4444', bg: 'danger', icon: '⛔', step: 0 }
};

const metaFor = (status) => STATUS_META[status] || STATUS_META.Pending;

/* Order the summary tiles appear in the hero */
const SUMMARY_ORDER = ['Pending', 'Approved', 'Refunded', 'Rejected'];
const SUMMARY_LABEL = {
  Pending: 'Pending',
  Approved: 'Approved',
  Refunded: 'Refunded',
  Rejected: 'Rejected'
};

/* Three-stop progress rail shown on each refund card */
const STEPS = ['Requested', 'Approved', 'Refunded'];

function RefundProgress({ status }) {
  if (status === 'Rejected') {
    return (
      <div className="mrf-rejected">
        <span className="mrf-rejected-dot">⛔</span>
        Request Rejected
      </div>
    );
  }

  const reached = metaFor(status).step;

  return (
    <div className="mrf-steps">
      {STEPS.map((label, i) => {
        const done = i + 1 <= reached;
        const current = i + 1 === reached;

        return (
          <React.Fragment key={label}>
            {i > 0 && (
              <span className={`mrf-step-line ${done ? 'is-done' : ''}`} />
            )}
            <div className={`mrf-step ${done ? 'is-done' : ''} ${current ? 'is-current' : ''}`}>
              <span className="mrf-step-dot">{done ? '✓' : i + 1}</span>
              <span className="mrf-step-label">{label}</span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

function MyRefundsPage() {
  const [refunds, setRefunds] = useState([]);
  const [selectedRefund, setSelectedRefund] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRefunds();
  }, []);

  const fetchRefunds = async () => {
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo'));

      const { data } = await axios.get(
        `${API}/refunds/user/${userInfo._id}`
      );

      setRefunds(data);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  /* Count refunds per status for the summary tiles */
  const counts = refunds.reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {});

  if (loading) {
    return (
      <>
        <CustomNavbar />

        <div className="mrf-wrap">
          <Container>
            <div className="mrf-skel mrf-skel-hero mb-4" />
            <Row className="g-4">
              {[0, 1, 2].map((n) => (
                <Col xl={4} lg={6} md={6} xs={12} key={n}>
                  <div className="mrf-skel mrf-skel-card" />
                </Col>
              ))}
            </Row>
          </Container>
        </div>

        <style>{myRefundsStyles}</style>
      </>
    );
  }

  return (
    <>
      <CustomNavbar />

      <div className="mrf-wrap">
        <Container>
          {/* Header */}
          <Card className="border-0 shadow-lg mb-4 mrf-hero">
            <span className="mrf-hero-blob mrf-hero-blob-1" />
            <span className="mrf-hero-blob mrf-hero-blob-2" />

            <Card.Body className="position-relative p-4 p-md-5">
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                <div>
                  <h1 className="fw-bold mb-1">💰 My Refunds</h1>
                  <p className="mb-0 mrf-hero-text">
                    Track the status of your refund requests
                  </p>
                </div>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <Badge className="mrf-count-badge">
                    {refunds.length}{' '}
                    {refunds.length === 1 ? 'Request' : 'Requests'}
                  </Badge>

                  <Link to="/apply-refund">
                    <Button className="mrf-apply-btn fw-semibold">
                      ↩ Apply for Refund
                    </Button>
                  </Link>
                </div>
              </div>

              {refunds.length > 0 && (
                <div className="mrf-summary">
                  {SUMMARY_ORDER.map((status) => (
                    <div className="mrf-summary-tile" key={status}>
                      <span className="mrf-summary-icon">
                        {metaFor(status).icon}
                      </span>
                      <div>
                        <div className="mrf-summary-num">
                          {counts[status] || 0}
                        </div>
                        <div className="mrf-summary-label">
                          {SUMMARY_LABEL[status]}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>

          {/* Refund cards */}
          {refunds.length === 0 ? (
            <Card className="border-0 shadow-sm text-center py-5 mrf-empty">
              <Card.Body>
                <div className="mrf-empty-icon">💸</div>
                <h5 className="fw-bold mt-3">No Refund Requests</h5>
                <p className="text-muted">
                  Your refund requests will appear here.
                </p>
                <Link to="/apply-refund">
                  <Button className="mrf-view-btn fw-semibold px-4">
                    ↩ Apply for Refund
                  </Button>
                </Link>
              </Card.Body>
            </Card>
          ) : (
            <Row className="g-4">
              {refunds.map((refund) => {
                const meta = metaFor(refund.status);

                return (
                  <Col xl={4} lg={6} md={6} xs={12} key={refund._id}>
                    <Card
                      className="border-0 shadow-sm h-100 mrf-card"
                      style={{ '--mrf-accent': meta.color }}
                    >
                      <span className="mrf-card-strip" />

                      <Card.Body className="d-flex flex-column p-4">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="mrf-order">
                            {refund.orderNumber || 'N/A'}
                          </span>
                          <Badge
                            bg={meta.bg}
                            className="rounded-pill mrf-status-badge"
                          >
                            {meta.icon} {refund.status}
                          </Badge>
                        </div>

                        <h6 className="fw-bold mrf-product mb-2">
                          📦 {refund.productName}
                        </h6>

                        <div className="mrf-detail mb-3">
                          <div className="mrf-label">Reason</div>
                          <div className="fw-semibold">{refund.reason}</div>
                        </div>

                        <RefundProgress status={refund.status} />

                        <div className="d-flex align-items-center justify-content-between mt-3 mb-3">
                          <small className="text-muted">
                            📅 {new Date(refund.createdAt).toLocaleDateString()}
                          </small>
                          {refund.images && refund.images.length > 0 && (
                            <small className="mrf-photo-count">
                              📷 {refund.images.length} photo
                              {refund.images.length > 1 ? 's' : ''}
                            </small>
                          )}
                        </div>

                        <Button
                          size="sm"
                          className="w-100 fw-semibold mt-auto mrf-view-btn"
                          onClick={() => setSelectedRefund(refund)}
                        >
                          <FaEye className="me-1" /> View Details
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}
        </Container>
      </div>

      <Modal
        centered
        show={!!selectedRefund}
        onHide={() => setSelectedRefund(null)}
        contentClassName="mrf-modal"
      >
        {selectedRefund && (
          <>
            <div
              className="mrf-modal-head"
              style={{ '--mrf-accent': metaFor(selectedRefund.status).color }}
            >
              <button
                type="button"
                className="mrf-modal-close"
                aria-label="Close"
                onClick={() => setSelectedRefund(null)}
              >
                ×
              </button>
              <div className="mrf-modal-icon">
                {metaFor(selectedRefund.status).icon}
              </div>
              <h5 className="fw-bold mb-1">{selectedRefund.productName}</h5>
              <Badge
                bg={metaFor(selectedRefund.status).bg}
                className="rounded-pill"
              >
                {selectedRefund.status}
              </Badge>
            </div>

            <Modal.Body className="p-4">
              <div className="mrf-modal-track mb-4">
                <RefundProgress status={selectedRefund.status} />
              </div>

              <div className="mrf-info-grid">
                <div className="mrf-info">
                  <div className="mrf-label">Order Number</div>
                  <div className="fw-semibold">
                    {selectedRefund.orderNumber || 'N/A'}
                  </div>
                </div>
                <div className="mrf-info">
                  <div className="mrf-label">Requested On</div>
                  <div className="fw-semibold">
                    {new Date(selectedRefund.createdAt).toLocaleString()}
                  </div>
                </div>
                <div className="mrf-info mrf-info-full">
                  <div className="mrf-label">Reason</div>
                  <div className="fw-semibold">{selectedRefund.reason}</div>
                </div>
                {selectedRefund.description && (
                  <div className="mrf-info mrf-info-full">
                    <div className="mrf-label">Description</div>
                    <div>{selectedRefund.description}</div>
                  </div>
                )}
              </div>

              {selectedRefund.images && selectedRefund.images.length > 0 && (
                <div className="mt-3">
                  <div className="mrf-label mb-2">Photos</div>
                  <div className="d-flex flex-wrap gap-2">
                    {selectedRefund.images.map((img, i) => (
                      <a
                        key={i}
                        href={img}
                        target="_blank"
                        rel="noreferrer"
                        title="Open full size"
                      >
                        <img
                          src={img}
                          alt={`evidence ${i + 1}`}
                          className="myref-evidence"
                        />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </Modal.Body>

            <Modal.Footer className="border-0">
              <Button
                variant="secondary"
                onClick={() => setSelectedRefund(null)}
              >
                Close
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>

      <style>{myRefundsStyles}</style>
    </>
  );
}

const myRefundsStyles = `
  .mrf-wrap {
    background: radial-gradient(720px 320px at 8% -5%, rgba(124,58,237,0.12), transparent 70%), radial-gradient(720px 320px at 92% 105%, rgba(234,88,12,0.14), transparent 70%), linear-gradient(180deg, #faf5ff 0%, #fff7ed 55%, #fef3c7 100%);
    min-height: 100vh;
    padding: clamp(24px, 4vw, 48px) 0;
  }

  .myref-evidence {
    width: 72px;
    height: 72px;
    object-fit: cover;
    border-radius: 10px;
    border: 1px solid #e5e7eb;
    cursor: pointer;
    transition: transform 0.15s ease;
  }
  .myref-evidence:hover { transform: scale(1.06); }

  /* Header */
  .mrf-hero {
    border-radius: 22px;
    overflow: hidden;
    color: #fff;
    background: linear-gradient(120deg, #0f172a, #7c2d12 60%, #ea580c);
    position: relative;
  }
  .mrf-hero-blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(60px);
    opacity: 0.45;
    pointer-events: none;
  }
  .mrf-hero-blob-1 {
    width: 200px; height: 200px;
    background: #f59e0b;
    top: -80px; left: 20%;
    animation: mrfFloat 9s ease-in-out infinite;
  }
  .mrf-hero-blob-2 {
    width: 180px; height: 180px;
    background: #22c55e;
    bottom: -90px; right: 8%;
    opacity: 0.3;
    animation: mrfFloat 11s ease-in-out infinite reverse;
  }
  .mrf-hero-text { opacity: 0.85; }
  .mrf-count-badge {
    background: rgba(255,255,255,0.18) !important;
    border: 1px solid rgba(255,255,255,0.3);
    border-radius: 30px;
    padding: 10px 18px;
    font-size: 1rem;
    font-weight: 600;
  }
  .mrf-apply-btn {
    background: #fff !important;
    border: none !important;
    color: #7c2d12 !important;
    border-radius: 30px;
    padding: 10px 20px;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }
  .mrf-apply-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(0,0,0,0.2);
  }

  /* Summary tiles inside the hero */
  .mrf-summary {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    margin-top: 26px;
  }
  .mrf-summary-tile {
    display: flex;
    align-items: center;
    gap: 12px;
    background: rgba(255,255,255,0.12);
    border: 1px solid rgba(255,255,255,0.18);
    border-radius: 16px;
    padding: 14px 16px;
    backdrop-filter: blur(6px);
    transition: transform 0.2s ease, background 0.2s ease;
  }
  .mrf-summary-tile:hover {
    transform: translateY(-3px);
    background: rgba(255,255,255,0.18);
  }
  .mrf-summary-icon { font-size: 1.6rem; line-height: 1; }
  .mrf-summary-num { font-size: 1.5rem; font-weight: 800; line-height: 1; }
  .mrf-summary-label {
    font-size: 0.75rem;
    opacity: 0.82;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    margin-top: 2px;
  }
  @media (max-width: 767.98px) {
    .mrf-summary { grid-template-columns: repeat(2, 1fr); }
  }

  /* Empty state */
  .mrf-empty { border-radius: 18px; }
  .mrf-empty-icon {
    font-size: 3.4rem;
    animation: mrfFloat 4s ease-in-out infinite;
  }

  /* Refund cards */
  .mrf-card {
    position: relative;
    border-radius: 16px;
    overflow: hidden;
    transition: transform 0.25s ease, box-shadow 0.25s ease;
  }
  .mrf-card-strip {
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 5px;
    background: var(--mrf-accent);
  }
  .mrf-card:hover {
    transform: translateY(-6px);
    box-shadow: 0 22px 40px rgba(194, 65, 12, 0.18) !important;
  }
  .mrf-order {
    font-weight: 800;
    color: #64748b;
    font-size: 0.85rem;
    letter-spacing: 0.3px;
  }
  .mrf-status-badge { font-weight: 600; }
  .mrf-product {
    font-size: 1.05rem;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .mrf-detail {
    background: rgba(248,250,252,0.85);
    border-radius: 10px;
    padding: 8px 12px;
  }
  .mrf-label {
    font-size: 0.66rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #94a3b8;
  }
  .mrf-photo-count {
    color: #b45309;
    font-weight: 600;
    background: #fff7ed;
    border: 1px solid #fed7aa;
    border-radius: 20px;
    padding: 2px 10px;
  }
  .mrf-view-btn {
    border: none;
    border-radius: 10px;
    background: linear-gradient(135deg, #0f172a, #7c2d12);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }
  .mrf-view-btn:hover {
    transform: translateY(-2px);
    background: linear-gradient(135deg, #1e293b, #9a3412);
    box-shadow: 0 10px 20px rgba(15,23,42,0.25);
  }

  /* Status progress rail */
  .mrf-steps {
    display: flex;
    align-items: center;
    margin-top: 4px;
  }
  .mrf-step {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;
    flex: 0 0 auto;
  }
  .mrf-step-dot {
    width: 26px; height: 26px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    font-weight: 700;
    background: #e5e7eb;
    color: #94a3b8;
    transition: background 0.3s ease, color 0.3s ease, transform 0.3s ease;
  }
  .mrf-step.is-done .mrf-step-dot {
    background: var(--mrf-accent);
    color: #fff;
  }
  .mrf-step.is-current .mrf-step-dot {
    transform: scale(1.12);
    box-shadow: 0 0 0 4px color-mix(in srgb, var(--mrf-accent) 22%, transparent);
  }
  .mrf-step-label {
    font-size: 0.66rem;
    font-weight: 600;
    color: #94a3b8;
    white-space: nowrap;
  }
  .mrf-step.is-done .mrf-step-label { color: #475569; }
  .mrf-step-line {
    flex: 1 1 auto;
    height: 3px;
    border-radius: 3px;
    background: #e5e7eb;
    margin: 0 6px 22px;
    transition: background 0.3s ease;
  }
  .mrf-step-line.is-done { background: var(--mrf-accent); }

  .mrf-rejected {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    background: #fef2f2;
    border: 1px solid #fecaca;
    color: #b91c1c;
    font-weight: 700;
    border-radius: 12px;
    padding: 10px;
  }
  .mrf-rejected-dot { font-size: 1.1rem; }

  /* Detail modal */
  .mrf-modal {
    border: none;
    border-radius: 20px;
    overflow: hidden;
  }
  .mrf-modal-head {
    position: relative;
    text-align: center;
    padding: 30px 24px 22px;
    color: #fff;
    background: linear-gradient(135deg, #0f172a, var(--mrf-accent));
  }
  .mrf-modal-close {
    position: absolute;
    top: 12px; right: 16px;
    background: rgba(255,255,255,0.2);
    border: none;
    color: #fff;
    width: 32px; height: 32px;
    border-radius: 50%;
    font-size: 1.3rem;
    line-height: 1;
    cursor: pointer;
    transition: background 0.2s ease;
  }
  .mrf-modal-close:hover { background: rgba(255,255,255,0.35); }
  .mrf-modal-icon {
    font-size: 2.4rem;
    width: 68px; height: 68px;
    margin: 0 auto 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255,255,255,0.18);
    border-radius: 50%;
  }
  .mrf-modal-track {
    background: #f8fafc;
    border-radius: 14px;
    padding: 16px 14px;
    display: flex;
    justify-content: center;
  }
  .mrf-info-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }
  .mrf-info {
    background: #f8fafc;
    border-radius: 12px;
    padding: 12px 14px;
  }
  .mrf-info-full { grid-column: 1 / -1; }

  @keyframes mrfFloat {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-12px); }
  }

  /* Loading skeletons */
  .mrf-skel {
    background: linear-gradient(90deg, rgba(148,163,184,0.14) 25%, rgba(255,255,255,0.6) 37%, rgba(148,163,184,0.14) 63%);
    background-size: 400% 100%;
    animation: mrfShimmer 1.4s ease infinite;
    border-radius: 18px;
  }
  .mrf-skel-hero { height: 180px; }
  .mrf-skel-card { height: 300px; border-radius: 16px; }
  @keyframes mrfShimmer {
    0% { background-position: 100% 0; }
    100% { background-position: -100% 0; }
  }

  @media (prefers-reduced-motion: reduce) {
    .mrf-hero-blob, .mrf-empty-icon, .mrf-skel { animation: none; }
  }
`;

export default MyRefundsPage;
